import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useApiQuery } from "@/lib/useApiQuery";
import { toast } from "sonner";
import { MailCheck, X } from "lucide-react";

/** Invitations that have been emailed but not yet accepted. */
export function PendingInvites() {
  const queryClient = useQueryClient();
  const { data } = useApiQuery(["invites"], "/users/invites");
  const invites = data || [];

  const revoke = async (id, email) => {
    try {
      await api.delete(`/users/invites/${id}`);
      toast.success("Invitation revoked", { description: email });
      queryClient.invalidateQueries({ queryKey: ["invites"] });
    } catch (e) {
      toast.error("Could not revoke", { description: e?.response?.data?.detail || e.message });
    }
  };

  if (invites.length === 0) return null;

  return (
    <div className="mt-8 k-card overflow-hidden" data-testid="pending-invites">
      <div className="px-6 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB] flex items-center gap-2">
        <MailCheck className="h-3.5 w-3.5 text-[#156F5B]" />
        <span className="k-sublabel">Pending invitations ({invites.length})</span>
      </div>
      {invites.map((i) => (
        <div key={i.id} className="px-6 py-3 flex items-center justify-between gap-4 border-b border-[#F0F2F1] last:border-0" data-testid={`invite-row-${i.email}`}>
          <div className="min-w-0">
            <div className="text-sm font-heading font-semibold text-[#212121] truncate">{i.name}</div>
            <div className="k-sublabel mt-0.5">
              {i.email} · {i.role} · invited by {i.invited_by}
              {i.expires_at && ` · expires ${new Date(i.expires_at).toLocaleString()}`}
            </div>
          </div>
          <button
            onClick={() => revoke(i.id, i.email)}
            data-testid={`invite-revoke-${i.email}`}
            className="p-1.5 rounded-md border border-[#E5E7EB] text-[#6B7280] hover:border-red-300 hover:text-red-600 shrink-0"
            title="Revoke invitation"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
